import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Tag,
  Tooltip,
  Tree,
  Typography,
  message,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  AppstoreAddOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  BarChartOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import {
  useSubsystemTree,
  useCreateSubsystem,
  useUpdateSubsystem,
  useDeleteSubsystem,
  useReorderSubsystems,
  useCreateSection,
  useDeleteSection,
  useCreateItem,
  useUpdateItem,
  useDeleteItem,
} from '@hooks/useSubsystems';
import { useDocumentTypes } from '@hooks/useMetadata';
import {
  type SubsystemDto,
  type SubsystemSectionDto,
  type SubsystemItemDto,
  SubsystemSectionType,
  SubsystemItemType,
  type SubsystemSectionTypeValue,
  type SubsystemItemTypeValue,
} from '@api/subsystemsApi';
import type { DataNode } from 'antd/es/tree';
import IconPicker from '@components/forms/IconPicker';
import { acqRenderIcon } from '@components/forms/IconRegistry';

// ── Маркери / нумерація ────────────────────────────────────────────────
const ACQ_BAS_PREFIX = '[bas:';

const ACQ_SECTION_LABELS: Record<SubsystemSectionTypeValue, string> = {
  [SubsystemSectionType.Catalogs]: 'Довідники',
  [SubsystemSectionType.Documents]: 'Документи',
  [SubsystemSectionType.Reports]: 'Звіти',
  [SubsystemSectionType.DataProcessors]: 'Обробки',
};

const ACQ_SECTION_ICONS: Record<SubsystemSectionTypeValue, JSX.Element> = {
  [SubsystemSectionType.Catalogs]: <DatabaseOutlined />,
  [SubsystemSectionType.Documents]: <FileTextOutlined />,
  [SubsystemSectionType.Reports]: <BarChartOutlined />,
  [SubsystemSectionType.DataProcessors]: <ToolOutlined />,
};

// Мапа frontend route key → людська назва довідника, щоб у select показувалось зрозуміле.
// Узгоджено з MainLayout.tsx (BAS_CATALOG_TO_KEY values + AcQuantum довідники).
const ACQ_CATALOG_OPTIONS: { value: string; label: string }[] = [
  { value: 'acc-organizations', label: 'Організації' },
  { value: 'acc-counterparties', label: 'Контрагенти' },
  { value: 'acc-currencies', label: 'Валюти' },
  { value: 'acc-banks', label: 'Банки' },
  { value: 'acc-nomenclature', label: 'Номенклатура' },
  { value: 'acc-warehouses', label: 'Склади' },
  { value: 'acc-cost-items', label: 'Статті витрат' },
  { value: 'acc-chart', label: 'План рахунків' },
  { value: 'acc-contracts', label: 'Договори' },
  { value: 'acc-other', label: 'Інші довідники' },
  { value: 'acc-individuals', label: 'Фіз. особи' },
  { value: 'acc-employees', label: 'Співробітники' },
  { value: 'acc-positions', label: 'Посади' },
  { value: 'acc-fixed-assets', label: 'Основні засоби' },
  { value: 'acc-intangible-assets', label: 'Нематеріальні активи' },
];

const ACQ_REPORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'rpt-trial-balance', label: 'Оборотно-сальдова відомість' },
  { value: 'rpt-journal', label: 'Журнал проводок' },
];

// ── Page ───────────────────────────────────────────────────────────────
export default function SubsystemConstructorPage() {
  const [acqIncludeInactive, setAcqIncludeInactive] = useState(false);
  const acqTreeQ = useSubsystemTree(acqIncludeInactive);
  const [acqSelectedId, setAcqSelectedId] = useState<string | null>(null);
  const [acqCreateModalOpen, setAcqCreateModalOpen] = useState(false);
  const [acqCreateParentId, setAcqCreateParentId] = useState<string | null>(null);
  const acqReorder = useReorderSubsystems();

  const acqAllSubsystems = useMemo(() => {
    const acqOut: SubsystemDto[] = [];
    const acqWalk = (acqList: SubsystemDto[]) => {
      for (const acqS of acqList) {
        acqOut.push(acqS);
        if (acqS.children?.length) acqWalk(acqS.children);
      }
    };
    acqWalk(acqTreeQ.data ?? []);
    return acqOut;
  }, [acqTreeQ.data]);

  const acqSelected = useMemo(
    () => acqAllSubsystems.find((s) => s.id === acqSelectedId) ?? null,
    [acqAllSubsystems, acqSelectedId]
  );

  const acqTreeData: DataNode[] = useMemo(() => {
    const acqToNode = (acqS: SubsystemDto): DataNode => ({
      key: acqS.id,
      title: (
        <span>
          {acqS.name}{' '}
          {acqS.isFromBas && <Tag color="default" style={{ marginLeft: 4 }}>BAS</Tag>}
          {!acqS.isActive && <Tag color="orange">не активна</Tag>}
        </span>
      ),
      children: acqS.children?.length ? acqS.children.map(acqToNode) : undefined,
    });
    return (acqTreeQ.data ?? []).map(acqToNode);
  }, [acqTreeQ.data]);

  /**
   * AntD Tree drag-and-drop. Логіка:
   *   • dropToGap=true → вставка як sibling до target (parent = target.parentId)
   *   • dropToGap=false → вставка як child target (parent = target.id)
   *   • dropPosition: -1 = before target, 1 = after target, 0 = into target
   * Frontend складає новий впорядкований список siblings і викликає
   * /api/subsystems/reorder одним запитом.
   */
  const acqHandleDrop: NonNullable<React.ComponentProps<typeof Tree>['onDrop']> = (info) => {
    const acqDragId = String(info.dragNode.key);
    const acqTargetId = String(info.node.key);
    const acqDropToGap = info.dropToGap;
    const acqDropPosition = info.dropPosition;

    const acqDrag = acqAllSubsystems.find((s) => s.id === acqDragId);
    const acqTarget = acqAllSubsystems.find((s) => s.id === acqTargetId);
    if (!acqDrag || !acqTarget) return;

    let acqNewParentId: string | null;
    let acqSiblings: SubsystemDto[];

    if (!acqDropToGap) {
      // drop в ноду = новий parent
      acqNewParentId = acqTargetId;
      acqSiblings = acqTarget.children ?? [];
    } else {
      // drop як sibling
      acqNewParentId = acqTarget.parentId ?? null;
      acqSiblings = acqNewParentId
        ? (acqAllSubsystems.find((s) => s.id === acqNewParentId)?.children ?? [])
        : (acqTreeQ.data ?? []);
    }

    // Прибираємо drag з siblings (якщо вже там — це reorder в межах того ж parent)
    const acqFiltered = acqSiblings.filter((s) => s.id !== acqDragId);

    // Вставляємо drag у потрібну позицію
    let acqInsertIndex: number;
    if (!acqDropToGap) {
      acqInsertIndex = acqFiltered.length; // у кінець children
    } else {
      const acqTargetIdx = acqFiltered.findIndex((s) => s.id === acqTargetId);
      if (acqTargetIdx < 0) {
        acqInsertIndex = acqFiltered.length;
      } else {
        // dropPosition: -1 = before, 1 = after
        acqInsertIndex = acqDropPosition <= 0 ? acqTargetIdx : acqTargetIdx + 1;
      }
    }

    const acqNewOrder = [...acqFiltered.slice(0, acqInsertIndex), acqDrag, ...acqFiltered.slice(acqInsertIndex)];

    acqReorder.mutate(
      { acqParentId: acqNewParentId, acqOrderedIds: acqNewOrder.map((s) => s.id) },
      {
        onSuccess: () => message.success('Порядок оновлено'),
        onError: (e: any) => message.error(e?.response?.data?.detail ?? e.message),
      }
    );
  };

  return (
    <Row gutter={16} style={{ minHeight: 600 }}>
      <Col span={9}>
        <Card
          size="small"
          title="Підсистеми"
          extra={
            <Space size="small">
              <Tooltip title="Показати неактивні">
                <Switch
                  size="small"
                  checked={acqIncludeInactive}
                  onChange={setAcqIncludeInactive}
                />
              </Tooltip>
              <Button
                size="small"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setAcqCreateParentId(null);
                  setAcqCreateModalOpen(true);
                }}
              >
                Нова підсистема
              </Button>
            </Space>
          }
        >
          {acqTreeQ.isLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
          ) : acqTreeData.length === 0 ? (
            <Empty description="Підсистем ще немає. Створіть першу або імпортуйте з BAS." />
          ) : (
            <Tree
              showLine
              defaultExpandAll
              draggable={{ icon: false }}
              blockNode
              treeData={acqTreeData}
              selectedKeys={acqSelectedId ? [acqSelectedId] : []}
              onSelect={(keys) => setAcqSelectedId((keys[0] as string) ?? null)}
              onDrop={acqHandleDrop}
            />
          )}
        </Card>
      </Col>

      <Col span={15}>
        {!acqSelected ? (
          <Card><Empty description="Оберіть підсистему зліва" /></Card>
        ) : (
          <SubsystemDetails
            acqSubsystem={acqSelected}
            acqOnAddChild={() => {
              setAcqCreateParentId(acqSelected.id);
              setAcqCreateModalOpen(true);
            }}
            acqOnDeleted={() => setAcqSelectedId(null)}
          />
        )}
      </Col>

      <CreateSubsystemModal
        acqOpen={acqCreateModalOpen}
        acqParentId={acqCreateParentId}
        acqOnClose={() => setAcqCreateModalOpen(false)}
      />
    </Row>
  );
}

// ── Right panel: details + sections + items ────────────────────────────
function SubsystemDetails({
  acqSubsystem,
  acqOnAddChild,
  acqOnDeleted,
}: {
  acqSubsystem: SubsystemDto;
  acqOnAddChild: () => void;
  acqOnDeleted: () => void;
}) {
  const acqUpdate = useUpdateSubsystem();
  const acqDelete = useDeleteSubsystem();
  const acqCreateSection = useCreateSection();
  const [acqForm] = Form.useForm();
  const [acqAddSectionType, setAcqAddSectionType] = useState<SubsystemSectionTypeValue | null>(null);

  // Reset form коли змінюється selected subsystem.
  useMemo(() => {
    acqForm.setFieldsValue({
      name: acqSubsystem.name,
      icon: acqSubsystem.icon,
      isActive: acqSubsystem.isActive,
    });
  }, [acqSubsystem, acqForm]);

  const acqExistingSectionTypes = new Set(acqSubsystem.sections.map((s) => s.type));
  const acqAvailableSectionTypes = (
    Object.keys(ACQ_SECTION_LABELS) as Array<`${number}`>
  )
    .map((k) => Number(k) as SubsystemSectionTypeValue)
    .filter((t) => !acqExistingSectionTypes.has(t));

  const acqHandleSave = async (acqValues: { name: string; icon?: string; isActive: boolean }) => {
    try {
      await acqUpdate.mutateAsync({
        id: acqSubsystem.id,
        name: acqValues.name,
        icon: acqValues.icon ?? null,
        parentId: acqSubsystem.parentId ?? null,
        orderIdx: acqSubsystem.orderIdx,
        isActive: acqValues.isActive,
      });
      message.success('Збережено');
    } catch (acqE: any) {
      message.error(acqE?.response?.data?.detail ?? acqE.message);
    }
  };

  const acqHandleDelete = async () => {
    try {
      await acqDelete.mutateAsync(acqSubsystem.id);
      message.success('Видалено');
      acqOnDeleted();
    } catch (acqE: any) {
      message.error(acqE?.response?.data?.detail ?? acqE.message);
    }
  };

  const acqHandleAddSection = async () => {
    if (!acqAddSectionType) return;
    try {
      await acqCreateSection.mutateAsync({
        subsystemId: acqSubsystem.id,
        type: acqAddSectionType,
        orderIdx: acqSubsystem.sections.length,
      });
      setAcqAddSectionType(null);
      message.success('Розділ додано');
    } catch (acqE: any) {
      message.error(acqE?.response?.data?.detail ?? acqE.message);
    }
  };

  return (
    <Card
      size="small"
      title={
        <Space>
          <Typography.Text strong>{acqSubsystem.name}</Typography.Text>
          <Tag color={acqSubsystem.isFromBas ? 'default' : 'blue'}>
            {acqSubsystem.isFromBas ? 'З BAS' : 'AcQuantum'}
          </Tag>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            code: {acqSubsystem.code}
          </Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Button size="small" icon={<PlusOutlined />} onClick={acqOnAddChild}>
            Дочірня
          </Button>
          {!acqSubsystem.isFromBas && (
            <Popconfirm title="Видалити підсистему та всі її розділи?" onConfirm={acqHandleDelete}>
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      }
    >
      {acqSubsystem.isFromBas && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Підсистема імпортована з BAS"
          description="Дозволено перейменування, зміна іконки та активність. Структуру (parent/order) не редагуємо — переімпорт BAS оновить її. Можна додавати власні розділи поруч із [bas:..] — вони не зачіпатимуться при re-import."
        />
      )}
      <Form layout="inline" form={acqForm} onFinish={acqHandleSave} style={{ marginBottom: 16 }}>
        <Form.Item name="name" label="Назва" rules={[{ required: true }]}>
          <Input style={{ width: 240 }} />
        </Form.Item>
        <Form.Item name="icon" label="Іконка">
          <Input placeholder="(опц.)" style={{ width: 120 }} />
        </Form.Item>
        <Form.Item name="isActive" label="Активна" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={acqUpdate.isPending} icon={<EditOutlined />}>
            Зберегти
          </Button>
        </Form.Item>
      </Form>

      <Tabs
        items={acqSubsystem.sections.length > 0
          ? acqSubsystem.sections
              .sort((a, b) => a.orderIdx - b.orderIdx)
              .map((acqSec) => ({
                key: acqSec.id,
                label: (
                  <span>
                    {ACQ_SECTION_ICONS[acqSec.type]} {acqSec.name?.startsWith(ACQ_BAS_PREFIX)
                      ? `${ACQ_SECTION_LABELS[acqSec.type]} (BAS)`
                      : acqSec.name ?? ACQ_SECTION_LABELS[acqSec.type]}
                    <Tag style={{ marginLeft: 6 }}>{acqSec.items.length}</Tag>
                  </span>
                ),
                children: <SectionItemsPanel acqSection={acqSec} />,
              }))
          : [{ key: 'empty', label: 'Розділів немає', children: <Empty description="Додайте розділ нижче" /> }]
        }
      />

      <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <Select
          placeholder="Тип нового розділу"
          value={acqAddSectionType ?? undefined}
          onChange={setAcqAddSectionType}
          options={acqAvailableSectionTypes.map((t) => ({
            value: t,
            label: ACQ_SECTION_LABELS[t],
          }))}
          style={{ width: 220 }}
          disabled={acqAvailableSectionTypes.length === 0}
        />
        <Button
          type="dashed"
          icon={<AppstoreAddOutlined />}
          onClick={acqHandleAddSection}
          disabled={!acqAddSectionType}
          loading={acqCreateSection.isPending}
        >
          Додати розділ
        </Button>
        {acqAvailableSectionTypes.length === 0 && (
          <Typography.Text type="secondary">Усі 4 типи розділів уже додані.</Typography.Text>
        )}
      </div>
    </Card>
  );
}

// ── Section panel: list of items + add-modal ───────────────────────────
function SectionItemsPanel({ acqSection }: { acqSection: SubsystemSectionDto }) {
  const acqDeleteSection = useDeleteSection();
  const acqCreateItem = useCreateItem();
  const acqUpdateItem = useUpdateItem();
  const acqDeleteItem = useDeleteItem();
  const [acqAddModal, setAcqAddModal] = useState(false);
  const [acqRef, setAcqRef] = useState<string | null>(null);
  const [acqDisplayName, setAcqDisplayName] = useState('');
  const [acqEditingItem, setAcqEditingItem] = useState<SubsystemItemDto | null>(null);

  const acqDocTypesQ = useDocumentTypes();
  const acqIsBasSection = acqSection.name?.startsWith(ACQ_BAS_PREFIX) ?? false;

  const acqOptions: { value: string; label: string }[] = useMemo(() => {
    if (acqSection.type === SubsystemSectionType.Catalogs) return ACQ_CATALOG_OPTIONS;
    if (acqSection.type === SubsystemSectionType.Reports) return ACQ_REPORT_OPTIONS;
    if (acqSection.type === SubsystemSectionType.Documents) {
      return (acqDocTypesQ.data ?? []).map((acqT) => ({ value: acqT.code, label: acqT.name }));
    }
    return [];
  }, [acqSection.type, acqDocTypesQ.data]);

  const acqHandleAdd = async () => {
    if (!acqRef) return;
    const acqItemType: SubsystemItemTypeValue =
      acqSection.type === SubsystemSectionType.Catalogs ? SubsystemItemType.Catalog
        : acqSection.type === SubsystemSectionType.Documents ? SubsystemItemType.Document
        : acqSection.type === SubsystemSectionType.Reports ? SubsystemItemType.Report
        : SubsystemItemType.DataProcessor;
    try {
      await acqCreateItem.mutateAsync({
        sectionId: acqSection.id,
        itemType: acqItemType,
        itemRef: acqRef,
        displayName: acqDisplayName || null,
        orderIdx: acqSection.items.length,
      });
      setAcqAddModal(false);
      setAcqRef(null);
      setAcqDisplayName('');
      message.success('Елемент додано');
    } catch (acqE: any) {
      message.error(acqE?.response?.data?.detail ?? acqE.message);
    }
  };

  return (
    <>
      <Space style={{ marginBottom: 8 }}>
        <Button size="small" icon={<PlusOutlined />} onClick={() => setAcqAddModal(true)}>
          Додати елемент
        </Button>
        {!acqIsBasSection && (
          <Popconfirm title="Видалити розділ та всі його елементи?" onConfirm={() => acqDeleteSection.mutateAsync(acqSection.id)}>
            <Button danger size="small" icon={<DeleteOutlined />}>Видалити розділ</Button>
          </Popconfirm>
        )}
        {acqIsBasSection && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            BAS-розділ: видалення заборонено (повториться при re-import)
          </Typography.Text>
        )}
      </Space>
      <List
        size="small"
        dataSource={acqSection.items.sort((a, b) => a.orderIdx - b.orderIdx)}
        locale={{ emptyText: <Empty description="Елементів немає" /> }}
        renderItem={(acqIt) => (
          <List.Item
            actions={[
              <Tooltip key="edit" title="Редагувати override назву / іконку">
                <Button
                  size="small"
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => setAcqEditingItem(acqIt)}
                />
              </Tooltip>,
              <Popconfirm
                key="del"
                title="Видалити елемент?"
                onConfirm={() => acqDeleteItem.mutateAsync(acqIt.id)}
              >
                <Button danger size="small" type="text" icon={<DeleteOutlined />} />
              </Popconfirm>,
            ]}
          >
            <Space>
              {acqIt.iconKey && (
                <Tooltip title={acqIt.iconKey}>
                  <span style={{ fontSize: 16, color: '#1677ff' }}>{acqRenderIcon(acqIt.iconKey)}</span>
                </Tooltip>
              )}
              <Tag>{acqIt.itemRef}</Tag>
              <Typography.Text>{acqIt.displayName ?? acqIt.itemRef}</Typography.Text>
            </Space>
          </List.Item>
        )}
      />

      <Modal
        title={`Додати елемент у розділ «${ACQ_SECTION_LABELS[acqSection.type]}»`}
        open={acqAddModal}
        onCancel={() => setAcqAddModal(false)}
        onOk={acqHandleAdd}
        okButtonProps={{ disabled: !acqRef, loading: acqCreateItem.isPending }}
      >
        <Form layout="vertical">
          <Form.Item label="Посилання" required>
            <Select
              showSearch
              optionFilterProp="label"
              value={acqRef ?? undefined}
              onChange={setAcqRef}
              options={acqOptions}
              placeholder={
                acqSection.type === SubsystemSectionType.Documents
                  ? 'Тип документа'
                  : acqSection.type === SubsystemSectionType.Catalogs
                    ? 'Довідник'
                    : 'Звіт'
              }
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item label="Override назва (опціонально)">
            <Input
              value={acqDisplayName}
              onChange={(e) => setAcqDisplayName(e.target.value)}
              placeholder="Залиште порожнім, щоб використати назву елемента"
            />
          </Form.Item>
        </Form>
      </Modal>

      <EditItemModal
        acqItem={acqEditingItem}
        acqOnClose={() => setAcqEditingItem(null)}
        acqOnSave={async (acqDisplayNameNew, acqIconKeyNew) => {
          if (!acqEditingItem) return;
          try {
            await acqUpdateItem.mutateAsync({
              id: acqEditingItem.id,
              displayName: acqDisplayNameNew,
              iconKey: acqIconKeyNew,
              orderIdx: acqEditingItem.orderIdx,
            });
            setAcqEditingItem(null);
            message.success('Елемент оновлено');
          } catch (acqE: any) {
            message.error(acqE?.response?.data?.detail ?? acqE.message);
          }
        }}
        acqIsSaving={acqUpdateItem.isPending}
      />
    </>
  );
}

// ── Edit item modal: override назва / icon (itemRef незмінний — це stable ref) ──
function EditItemModal({
  acqItem, acqOnClose, acqOnSave, acqIsSaving,
}: {
  acqItem: SubsystemItemDto | null;
  acqOnClose: () => void;
  acqOnSave: (acqDisplayName: string | null, acqIconKey: string | null) => void;
  acqIsSaving: boolean;
}) {
  const [acqDisplayName, setAcqDisplayName] = useState('');
  const [acqIconKey, setAcqIconKey] = useState('');

  // Reset форму при відкритті
  useMemo(() => {
    if (acqItem) {
      setAcqDisplayName(acqItem.displayName ?? '');
      setAcqIconKey(acqItem.iconKey ?? '');
    }
  }, [acqItem]);

  return (
    <Modal
      title={acqItem ? `Редагувати елемент: ${acqItem.itemRef}` : 'Редагувати елемент'}
      open={!!acqItem}
      onCancel={acqOnClose}
      onOk={() => acqOnSave(
        acqDisplayName.trim() ? acqDisplayName.trim() : null,
        acqIconKey.trim() ? acqIconKey.trim() : null,
      )}
      okButtonProps={{ loading: acqIsSaving }}
      destroyOnClose
    >
      <Form layout="vertical">
        <Form.Item label="Посилання (itemRef)" extra="Незмінне — щоб змінити посилання, видаліть і створіть елемент заново">
          <Input value={acqItem?.itemRef ?? ''} disabled />
        </Form.Item>
        <Form.Item label="Override назва (опціонально)">
          <Input
            value={acqDisplayName}
            onChange={(e) => setAcqDisplayName(e.target.value)}
            placeholder="Залиште порожнім, щоб використати назву елемента"
          />
        </Form.Item>
        <Form.Item label="Іконка (опціонально)" extra="Курований реєстр AntD-іконок (10 категорій)">
          <IconPicker
            acqValue={acqIconKey || null}
            acqOnChange={(k) => setAcqIconKey(k ?? '')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Create subsystem modal ─────────────────────────────────────────────
function CreateSubsystemModal({
  acqOpen,
  acqParentId,
  acqOnClose,
}: {
  acqOpen: boolean;
  acqParentId: string | null;
  acqOnClose: () => void;
}) {
  const [acqForm] = Form.useForm();
  const acqCreate = useCreateSubsystem();

  const acqHandleOk = async () => {
    try {
      const acqV = await acqForm.validateFields();
      await acqCreate.mutateAsync({
        code: acqV.code,
        name: acqV.name,
        parentId: acqParentId,
        icon: acqV.icon || null,
      });
      message.success('Створено');
      acqForm.resetFields();
      acqOnClose();
    } catch (acqE: any) {
      if (acqE?.response?.data) {
        message.error(acqE.response.data.detail ?? acqE.response.data.title ?? 'Помилка');
      }
    }
  };

  return (
    <Modal
      title={acqParentId ? 'Нова дочірня підсистема' : 'Нова підсистема'}
      open={acqOpen}
      onCancel={acqOnClose}
      onOk={acqHandleOk}
      okButtonProps={{ loading: acqCreate.isPending }}
    >
      <Form form={acqForm} layout="vertical">
        <Form.Item
          label="Код"
          name="code"
          rules={[
            { required: true, message: 'Код обовʼязковий' },
            { pattern: /^[a-z0-9-]+$/, message: 'Малі латинські літери, цифри, дефіс' },
          ]}
          extra="Стабільний ідентифікатор (slug). Не міняти після створення."
        >
          <Input placeholder="напр. trade, finance, my-section" />
        </Form.Item>
        <Form.Item label="Назва" name="name" rules={[{ required: true }]}>
          <Input placeholder="Що бачитиме користувач у меню" />
        </Form.Item>
        <Form.Item label="Іконка (опц.)" name="icon">
          <Input placeholder="AntD icon name або emoji" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
